import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { CeRequestStateService } from '../../services/ce-request-state.service';
import { CurrentUserService } from '../../services/current-user.service';
import { DictionariesApiService } from '../../services/dictionaries-api.service';
import { HttpNotificationService } from '../../services/http-notification.service';
import {
  ProjectAttachmentDto,
  ProjectResponseDto,
  ProjectsApiService,
  UpdateProjectResponseCommand,
} from '../../services/projects-api.service';
import { FlowStep, getResponseSteps } from '../../data/flow-steps';

type ResponseStatus = 'Draft' | 'Submitted' | 'Assigned CE' | 'Under Development' | 'Pending Additional Information' | 'Issued' | 'Canceled';

const RESPONSE_STATUS_OPTIONS: readonly ResponseStatus[] = [
  'Draft',
  'Submitted',
  'Assigned CE',
  'Under Development',
  'Pending Additional Information',
  'Issued',
  'Canceled',
];

interface ResponsePerson {
  name: string;
  positionName: string;
  initials: string;
}

interface ConversationComment {
  author: string;
  role: string;
  createdAtRaw: string;
  postedAt?: string;
  createdAt: number;
  message: string;
}

type CommentSortOrder = 'oldest' | 'newest';

interface SubmitDialogResult {
  comment: string;
  attachments: File[];
}

interface SubmitCommentDialogData {
  attachmentRequired: boolean;
}

interface ConfirmAttachmentDeleteDialogData {
  fileName: string;
}

const RESPONSE_ATTACHMENT_ENTITY_TYPE = 0;
const SUBMITTED_STATUS_TYPE = 1;
const ISSUED_STATUS_TYPE = 4;

@Component({
  selector: 'app-response-page',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatAutocompleteModule, MatDialogModule],
  templateUrl: './response-page.html',
  styleUrl: './response-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponsePageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly state = inject(CeRequestStateService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly notifications = inject(HttpNotificationService);
  private readonly dialog = inject(MatDialog);

  protected readonly user = this.currentUserService.profile;
  protected readonly statusOptions = RESPONSE_STATUS_OPTIONS;
  protected alternativeNumberOptions: string[] = [];
  protected typeOptions: string[] = [];
  protected revisionOptions: string[] = [];
  protected contingencyOptions: string[] = [];
  protected userOptions: string[] = [];
  protected readonly steps = computed(() => getResponseSteps(this.state.draftForm().isNewProject));
  protected readonly projectId = signal<number | null>(
    Number(this.route.snapshot.queryParamMap.get('projectId')) || null,
  );

  protected readonly showAllDropdownOptions = signal<Record<string, boolean>>({});
  protected readonly submitAttempted = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly commentSortOrder = signal<CommentSortOrder>('oldest');
  protected readonly savedStatus = signal<ResponseStatus | ''>('');
  protected readonly isReadOnly = signal(true);
  private readonly roleCanDeleteAttachments = signal(false);
  private readonly statusLocked = signal(false);
  protected readonly canDeleteAttachments = computed(() => this.roleCanDeleteAttachments() && !this.statusLocked());
  private readonly readOnlyByRole = signal(false);
  protected readonly responseEstimators = signal({
    tcoEstimator: '',
    tcoSecondaryEstimator: '',
    tcoReviewer: '',
  });
  protected readonly assignedEstimator = computed<ResponsePerson>(() => ({
    name: this.responseEstimators().tcoEstimator,
    positionName: this.user().positionName,
    initials: this.getInitials(this.responseEstimators().tcoEstimator),
  }));
  protected readonly previousComments = signal<ConversationComment[]>([]);
  protected readonly attachments = signal<ProjectAttachmentDto[]>([]);
  protected readonly hasAttachments = computed(() => this.attachments().length > 0);
  protected readonly sortedComments = computed(() => {
    const comments = [...this.previousComments()];
    const direction = this.commentSortOrder() === 'oldest' ? 1 : -1;
    return comments.sort((a, b) => (a.createdAt - b.createdAt) * direction);
  });

  constructor() {
    this.state.markResponseVisited();
    void this.initializePage();
    void this.loadAlternativeNumbers();
    void this.loadTypes();
    void this.loadRevisions();
    void this.loadContingencyCalculationMethods();
    void this.loadResponseUsers();

    const id = this.projectId();
    if (!id) {
      // Keep previous behavior for ad-hoc page open without project context.
      this.isReadOnly.set(this.readOnlyByRole());
    }
  }

  private async initializePage(): Promise<void> {
    await this.initializeReadOnlyByRole();

    const id = this.projectId();
    if (id) {
      void this.loadProjectSummary(id);
      void this.loadProjectResponse(id);
      void this.loadProjectAttachments(id);
    }
  }

  private async initializeReadOnlyByRole(): Promise<void> {
    await this.currentUserService.load();
    const isCeEngineer = this.currentUserService.hasAnyRole(['ce_engineer', 'ce_engineers']);
    const isProjectEngineer = this.currentUserService.hasAnyRole(['project_engineer', 'project_engineers']);

    this.roleCanDeleteAttachments.set(isCeEngineer && !isProjectEngineer);
    this.readOnlyByRole.set(!isCeEngineer);

    if (!isCeEngineer) {
      this.isReadOnly.set(true);
    }
  }

  protected readonly form = signal({
    status: '' as ResponseStatus | '',
    dateCompleted: '',
    alternativeNumber: '',
    type: '',
    revision: '',
    contingencyCalculationMethod: '',
    secondaryEstimator: '',
    reviewer: '',
  });

  protected readonly hasValidationErrors = computed(() => {
    const form = this.form();
    const isIssued = form.status === 'Issued';
    return !(
      form.status &&
      (!isIssued || form.dateCompleted) &&
      form.alternativeNumber &&
      form.type &&
      form.revision &&
      form.contingencyCalculationMethod
    );
  });

  protected readonly isIssuedStatus = computed(() => this.form().status === 'Issued');

  private readonly statusTransitions: Record<ResponseStatus, readonly ResponseStatus[]> = {
    'Draft': ['Submitted', 'Canceled'],
    'Submitted': ['Draft', 'Assigned CE', 'Canceled'],
    'Assigned CE': ['Under Development'],
    'Under Development': ['Pending Additional Information', 'Canceled', 'Issued'],
    'Pending Additional Information': ['Issued', 'Canceled'],
    'Issued': [],
    'Canceled': [],
  };

  protected readonly availableStatusOptions = computed(() => {
    const currentStatus = this.savedStatus();
    const hiddenStatuses = ['Draft', 'Submitted', 'Assigned CE'];
    
    if (!currentStatus) {
      return RESPONSE_STATUS_OPTIONS.filter(status => !hiddenStatuses.includes(status));
    }

    const allowed = this.statusTransitions[currentStatus] ?? [];
    return RESPONSE_STATUS_OPTIONS.filter(status => 
      allowed.includes(status) && !hiddenStatuses.includes(status)
    );
  });

  protected updateField<K extends keyof ReturnType<ResponsePageComponent['form']>>(field: K, value: ReturnType<ResponsePageComponent['form']>[K]): void {
    if (this.isReadOnly()) {
      return;
    }

    // For user fields, validate that the value is either empty or exists in userOptions
    if ((field === 'secondaryEstimator' || field === 'reviewer') && typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue && !this.userOptions.includes(trimmedValue)) {
        return; // Ignore invalid user values
      }
    }

    this.form.update(current => ({ ...current, [field]: value }));
  }

  protected openDropdown(field: string, trigger: MatAutocompleteTrigger): void {
    if (this.isReadOnly()) {
      return;
    }

    this.showAllDropdownOptions.update(current => ({ ...current, [field]: true }));
    trigger.openPanel();
  }

  protected closeDropdown(field: string): void {
    setTimeout(() => {
      this.showAllDropdownOptions.update(current => ({ ...current, [field]: false }));
    }, 120);
  }

  protected getDropdownOptions(options: readonly string[], field: string, value: string): readonly string[] {
    // For status field: always return available transitions without query filtering,
    // since the current status value should not filter the next available statuses.
    if (field === 'status') {
      return this.availableStatusOptions();
    }

    if (this.showAllDropdownOptions()[field]) {
      return options;
    }

    const query = value.trim().toLowerCase();
    if (!query) {
      return options;
    }

    // If current value is already a selected option, show the full list on reopen.
    if (options.some(option => option.toLowerCase() === query)) {
      return options;
    }

    return options.filter(option => option.toLowerCase().includes(query));
  }

  protected getInitials(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected toggleCommentSortOrder(): void {
    this.commentSortOrder.update(current => (current === 'oldest' ? 'newest' : 'oldest'));
  }

  protected isValidUser(value: string): boolean {
    return this.userOptions.includes(value.trim());
  }

  protected onDropdownChange(field: string, value: string): void {
    if (this.isReadOnly()) {
      return;
    }

    this.showAllDropdownOptions.update(current => ({ ...current, [field]: false }));
    this.form.update(current => {
      const next = { ...current, [field]: value };
      if (field === 'status' && value !== 'Issued') {
        next.dateCompleted = '';
      }
      return next;
    });
  }

  protected openDatePicker(event: Event): void {
    if (this.isReadOnly()) {
      return;
    }

    if (!this.isIssuedStatus()) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'date') {
      return;
    }

    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      try {
        pickerInput.showPicker();
      } catch (error) {
        console.error(error);
        // Ignore when browser blocks programmatic opening.
      }
    }
  }

  protected onUserFieldChange(field: 'secondaryEstimator' | 'reviewer', value: string): void {
    if (this.isReadOnly()) {
      return;
    }

    // Reset showAll so filtering activates while typing
    this.showAllDropdownOptions.update(current => ({ ...current, [field]: false }));
    this.form.update(current => ({ ...current, [field]: value }));
  }

  protected validateUserField(field: 'secondaryEstimator' | 'reviewer'): void {
    if (this.isReadOnly()) {
      return;
    }

    setTimeout(() => {
      const value = this.form()[field].trim();
      if (value && !this.userOptions.includes(value)) {
        this.form.update(current => ({ ...current, [field]: '' }));
      }
    }, 200);
  }

  protected cancel(): void {
    this.router.navigate(['/my-ce-requests-assigned']);
  }

  protected isStepClickable(step: FlowStep): boolean {
    return !!step.route;
  }

  protected goToStep(step: FlowStep): void {
    if (!this.isStepClickable(step)) {
      return;
    }

    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([step.route!], queryParams ? { queryParams } : {});
  }

  protected submit(): void {
    if (this.isReadOnly() || this.isSubmitting()) {
      return;
    }

    this.submitAttempted.set(true);
    if (this.hasValidationErrors()) {
      return;
    }

    const dialogRef = this.dialog.open(SubmitCommentDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        attachmentRequired: this.form().status === 'Issued',
      } satisfies SubmitCommentDialogData,
    });

    dialogRef.afterClosed().subscribe((result: SubmitDialogResult | undefined) => {
      if (result) {
        // User clicked Confirm
        void this.submitWithComment(result.comment, result.attachments);
      }
      // If undefined, user clicked Cancel - do nothing
    });
  }

  private async submitWithComment(comment: string, attachments: File[]): Promise<void> {
    const projectId = this.projectId();
    if (!projectId) {
      this.notifications.showError('Project ID is required to submit response.');
      return;
    }

    const form = this.form();
    let shouldRefresh = false;
    let uploadedAttachmentIds: number[] = [];
    let updateSucceeded = false;
    this.isSubmitting.set(true);

    try {
      if (attachments.length) {
        const uploadedAttachments = await firstValueFrom(
          this.projectsApi.uploadProjectAttachments(projectId, attachments, RESPONSE_ATTACHMENT_ENTITY_TYPE),
        );
        uploadedAttachmentIds = uploadedAttachments.map(item => item.id);
      }

      const payload: UpdateProjectResponseCommand = {
        id: projectId,
        tcoEstimator: this.assignedEstimator().name,
        tcoSecondaryEstimator: form.secondaryEstimator.trim(),
        tcoReviewer: form.reviewer.trim(),
        projectStatusType: this.mapResponseStatusToProjectStatusType(form.status),
        dateCompleted: form.dateCompleted ? new Date(form.dateCompleted).toISOString() : null,
        alternativeNumber: form.alternativeNumber,
        type: form.type,
        revision: form.revision,
        contingencyCalculationMethod: form.contingencyCalculationMethod,
        comments: comment.trim(),
      };

      await firstValueFrom(this.projectsApi.updateProjectResponse(projectId, payload));
      updateSucceeded = true;
      if (attachments.length) {
        await this.loadProjectAttachments(projectId);
      }
      shouldRefresh = true;
    } catch (error) {
      if (!updateSucceeded && uploadedAttachmentIds.length) {
        await this.deleteUploadedAttachments(projectId, uploadedAttachmentIds);
      }
      // Keep entered values and comments in UI even if save fails.
    } finally {
      this.isSubmitting.set(false);
    }

    if (shouldRefresh) {
      if (form.status === 'Issued' || form.status === 'Canceled') {
        this.router.navigate(['/']);
        return;
      }

      window.location.reload();
    }
  }

  protected isFieldInvalid(field: 'status' | 'alternativeNumber' | 'type' | 'revision' | 'contingencyCalculationMethod'): boolean {
    if (!this.submitAttempted()) {
      return false;
    }

    return !this.form()[field].trim();
  }

  protected isDateCompletedInvalid(): boolean {
    if (!this.submitAttempted()) {
      return false;
    }

    return this.isIssuedStatus() && !this.form().dateCompleted;
  }

  private async loadContingencyCalculationMethods(): Promise<void> {
    try {
      const values = await firstValueFrom(this.dictionariesApi.getContingencyCalculationMethods());
      this.contingencyOptions = values;
    } catch (error) {
      console.error(error);
      this.contingencyOptions = [];
    }
  }

  private async loadResponseUsers(): Promise<void> {
    try {
      const estimators = await firstValueFrom(this.projectsApi.getCostEstimators());
      this.userOptions = Array.from(
        new Set(
          estimators
            .map(estimator => estimator.fullName?.trim() ?? '')
            .filter((name): name is string => !!name),
        ),
      );
    } catch (error) {
      console.error(error);
      this.userOptions = [];
    }
  }

  private async loadTypes(): Promise<void> {
    try {
      const values = await firstValueFrom(this.dictionariesApi.getTypes());
      this.typeOptions = values;
    } catch (error) {
      console.error(error);
      this.typeOptions = [];
    }
  }

  private async loadAlternativeNumbers(): Promise<void> {
    try {
      const values = await firstValueFrom(this.dictionariesApi.getAlternativeNumbers());
      this.alternativeNumberOptions = values;
    } catch (error) {
      console.error(error);
      this.alternativeNumberOptions = [];
    }
  }

  private async loadRevisions(): Promise<void> {
    try {
      const values = await firstValueFrom(this.dictionariesApi.getRevisions());
      this.revisionOptions = values;
    } catch (error) {
      console.error(error);
      this.revisionOptions = [];
    }
  }

  private async loadProjectResponse(projectId: number): Promise<void> {
    try {
      const response = await firstValueFrom(this.projectsApi.getProjectResponse(projectId));
      this.applyProjectResponse(response);
    } catch (error) {
      console.error(error);
      // Keep defaults when API response is unavailable.
    }
  }

  private async loadProjectAttachments(projectId: number): Promise<void> {
    try {
      const attachments = await firstValueFrom(this.projectsApi.getProjectAttachments(projectId, RESPONSE_ATTACHMENT_ENTITY_TYPE));
      this.attachments.set(attachments ?? []);
    } catch (error) {
      console.error(error);
      this.attachments.set([]);
    }
  }

  protected async downloadAttachment(attachment: ProjectAttachmentDto): Promise<void> {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.projectsApi.downloadProjectAttachment(projectId, attachment.id, RESPONSE_ATTACHMENT_ENTITY_TYPE),
      );
      const blob = response.body ?? new Blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      this.notifications.showError('Failed to download attachment. Please try again.');
    }
  }

  protected async deleteAttachment(attachment: ProjectAttachmentDto): Promise<void> {
    const projectId = this.projectId();
    if (!projectId || this.isReadOnly() || !this.canDeleteAttachments()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmAttachmentDeleteDialogComponent, {
      width: '420px',
      disableClose: true,
      data: {
        fileName: attachment.fileName,
      } satisfies ConfirmAttachmentDeleteDialogData,
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    try {
      await firstValueFrom(
        this.projectsApi.deleteProjectAttachment(projectId, attachment.id, RESPONSE_ATTACHMENT_ENTITY_TYPE),
      );
      await this.loadProjectAttachments(projectId);
    } catch (error) {
      console.error(error);
    }
  }

  private async deleteUploadedAttachments(projectId: number, attachmentIds: number[]): Promise<void> {
    for (const attachmentId of attachmentIds) {
      try {
        await firstValueFrom(
          this.projectsApi.deleteProjectAttachment(projectId, attachmentId, RESPONSE_ATTACHMENT_ENTITY_TYPE, { skipNotification: true }),
        );
      } catch (error) {
        console.error(error);
      }
    }
  }

  protected formatAttachmentSize(bytes: number): string {
    if (!bytes || bytes < 0) {
      return '0 B';
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  private async loadProjectSummary(projectId: number): Promise<void> {
    try {
      const project = await firstValueFrom(this.projectsApi.getProjectById(projectId));
      this.state.draftForm.update(d => ({
        ...d,
        isNewProject: project.isNewProject ?? d.isNewProject,
      }));
    } catch (error) {
      console.error(error);
      // Keep existing value if summary API call fails.
    }
  }

  private applyProjectResponse(response: ProjectResponseDto): void {
    const lockedByStatus = this.isLockedStatus(response.projectStatusType);
    this.statusLocked.set(lockedByStatus);
    this.isReadOnly.set(this.readOnlyByRole() || response.tcoEstimator == null || lockedByStatus);

    const status = this.mapApiStatusToResponseStatus(response);
    const tcoEstimator = (response.tcoEstimator ?? '').trim();
    const tcoSecondaryEstimator = (response.tcoSecondaryEstimator ?? '').trim();
    const tcoReviewer = (response.tcoReviewer ?? '').trim();

    this.savedStatus.set(status);

    this.responseEstimators.set({
      tcoEstimator,
      tcoSecondaryEstimator,
      tcoReviewer,
    });

    this.form.update(current => ({
      ...current,
      status,
      dateCompleted: response.dateCompleted ? response.dateCompleted.split('T')[0] : '',
      alternativeNumber: response.alternativeNumber ?? '',
      type: response.type ?? '',
      revision: response.revision ?? '',
      contingencyCalculationMethod: response.contingencyCalculationMethod ?? '',
      secondaryEstimator: tcoSecondaryEstimator,
      reviewer: tcoReviewer,
    }));

    const comments = (response.commentHistory ?? []).map(comment => ({
      author: comment.createdBy || 'Unknown',
      role: 'User',
      createdAtRaw: comment.createdDate || '',
      createdAt: comment.createdDate ? new Date(comment.createdDate).getTime() : Date.now(),
      message: comment.text || '',
    }));

    this.previousComments.set(comments);
  }

  protected getCommentDate(comment: ConversationComment): string {
    const raw = comment.createdAtRaw || comment.postedAt || '';
    return this.formatCommentDate(raw);
  }

  private formatCommentDate(raw: string): string {
    if (!raw) {
      return '';
    }

    // Handle ISO-like values from API/UI: 2026-07-28T00:00:00 / 2026-07-29T16:46:25.112Z
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]} ${isoMatch[2]}`;
    }

    // Fallback for other parseable date formats.
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const dd = String(parsed.getDate()).padStart(2, '0');
      const hh = String(parsed.getHours()).padStart(2, '0');
      const min = String(parsed.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    }

    return raw;
  }

  private mapApiStatusToResponseStatus(response: ProjectResponseDto): ResponseStatus | '' {
    const statusType = response.projectStatusType;
    if (statusType == null) {
      return '';
    }

    const statusMap: Record<number, ResponseStatus> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Assigned CE',
      3: 'Under Development',
      4: 'Issued',
      5: 'Pending Additional Information',
      6: 'Canceled',
    };

    return statusMap[statusType] ?? '';
  }

  private mapResponseStatusToProjectStatusType(status: ResponseStatus | ''): number {
    const map: Record<ResponseStatus, number> = {
      Draft: 0,
      Submitted: 1,
      'Assigned CE': 2,
      'Under Development': 3,
      Issued: 4,
      'Pending Additional Information': 5,
      Canceled: 6,
    };

    if (!status) {
      return 2;
    }

    return map[status] ?? 2;
  }

  private isLockedStatus(projectStatusType?: number | null): boolean {
    return projectStatusType === SUBMITTED_STATUS_TYPE || projectStatusType === ISSUED_STATUS_TYPE;
  }

}

@Component({
  selector: 'app-submit-comment-dialog',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatDialogModule],
  template: `
    <h2 mat-dialog-title>Add Comment Before Submitting</h2>
    <mat-dialog-content>
      <div class="dialog-form">
        <label class="dialog-label">Comment <span class="required">*</span></label>
        <textarea
          class="dialog-textarea"
          [(ngModel)]="comment"
          placeholder="Enter your comment..."
          rows="6"
        ></textarea>
        @if (!comment.trim()) {
          <p class="field-error">Comment is required.</p>
        }

        <label class="dialog-label">
          Attachment
          @if (attachmentRequired) {
            <span class="required attachment-required">*</span>
          }
        </label>
        <input
          #attachmentInput
          type="file"
          multiple
          class="dialog-file-input"
          (change)="onAttachmentSelected($event)"
        />
        @if (attachmentRequired && !attachments.length) {
          <p class="field-error">Attachment is required when submitting Issued status.</p>
        }
        @if (attachments.length) {
          <div class="dialog-file-meta">
            <span>{{ attachments.length }} file(s) selected</span>
          </div>
          <div class="dialog-file-list">
            @for (file of attachments; track file.name + file.size + file.lastModified; let index = $index) {
              <span class="dialog-file-chip">
                <span>{{ file.name }}</span>
                <button type="button" class="dialog-file-chip-remove" (click)="removeAttachment(index)">Remove</button>
              </span>
            }
          </div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" class="btn btn-cancel-dialog" (click)="onCancel()">Cancel</button>
      <button
        type="button"
        class="btn btn-confirm-dialog"
        [disabled]="!comment.trim() || (attachmentRequired && !attachments.length)"
        (click)="onConfirm()"
      >
        Confirm
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }

    mat-dialog-content {
      padding: 1.5rem;
    }

    .dialog-form {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .dialog-label {
      font-weight: 600;
      color: #1f2937;
      font-size: 0.9rem;
    }

    .required {
      color: #dc2626;
    }

    .attachment-required {
      margin-left: 0.25rem;
    }

    .dialog-textarea {
      width: 100%;
      padding: 0.625rem 0.875rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.9rem;
      font-family: inherit;
      resize: vertical;

      &:focus {
        outline: none;
        border-color: #3a5a8c;
        box-shadow: 0 0 0 3px rgba(58, 90, 140, 0.1);
      }
    }

    .dialog-file-input {
      width: 100%;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      padding: 0.45rem 0.625rem;
      background: #fff;
      font-size: 0.85rem;
    }

    .dialog-file-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8rem;
      color: #334155;
    }

    .dialog-file-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
    }

    .dialog-file-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.45rem;
      max-width: 100%;
      padding: 0.25rem 0.5rem;
      border-radius: 999px;
      background: #eef2ff;
      color: #334155;
      font-size: 0.75rem;
      word-break: break-word;
    }

    .dialog-file-chip-remove {
      border: none;
      background: transparent;
      color: #b91c1c;
      font-size: 0.72rem;
      font-weight: 600;
      line-height: 1;
      cursor: pointer;
      padding: 0;
    }

    .btn-file-clear {
      border: none;
      background: transparent;
      color: #b91c1c;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
    }

    .field-error {
      margin: 0.2rem 0 0;
      color: #dc2626;
      font-size: 0.72rem;
    }

    mat-dialog-actions {
      padding: 1rem 1.5rem;
      gap: 0.5rem;
    }

    .btn {
      padding: 0.625rem 1.25rem;
      border-radius: 0.375rem;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      border: none;

      &.btn-cancel-dialog {
        background: #fff;
        color: #3a5a8c;
        border: 1px solid #d1d5db;

        &:hover {
          background: #f3f4f6;
        }
      }

      &.btn-confirm-dialog {
        background: #3a5a8c;
        color: #fff;

        &:hover:not(:disabled) {
          background: #2d4a6f;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }
  `],
})
export class SubmitCommentDialogComponent {
  private dialogRef = inject(MatDialogRef<SubmitDialogResult | undefined>);
  private readonly data = inject<SubmitCommentDialogData>(MAT_DIALOG_DATA);
  comment = '';
  attachments: File[] = [];
  protected readonly attachmentRequired = this.data.attachmentRequired;

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.attachments = input?.files ? Array.from(input.files) : [];
  }

  removeAttachment(index: number): void {
    this.attachments = this.attachments.filter((_, fileIndex) => fileIndex !== index);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onConfirm(): void {
    if (this.comment.trim() && (!this.attachmentRequired || this.attachments.length)) {
      this.dialogRef.close({
        comment: this.comment,
        attachments: this.attachments,
      });
    }
  }
}

@Component({
  selector: 'app-confirm-attachment-delete-dialog',
  standalone: true,
  imports: [MatDialogModule],
  template: `
    <h2 mat-dialog-title>Delete Attachment</h2>
    <mat-dialog-content>
      <p class="confirm-delete-text">
        Are you sure you want to delete
        <strong>{{ data.fileName }}</strong>?
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" class="btn btn-cancel-dialog" (click)="onCancel()">Cancel</button>
      <button type="button" class="btn btn-confirm-dialog" (click)="onConfirm()">Delete</button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }

    mat-dialog-content {
      padding: 1rem 1.5rem 0.25rem;
    }

    .confirm-delete-text {
      margin: 0;
      color: #334155;
      font-size: 0.92rem;
      line-height: 1.5;
      word-break: break-word;
    }

    mat-dialog-actions {
      padding: 1rem 1.5rem 1.25rem;
      gap: 0.5rem;
    }

    .btn {
      padding: 0.625rem 1.25rem;
      border-radius: 0.375rem;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      border: none;
    }

    .btn-cancel-dialog {
      background: #fff;
      color: #3a5a8c;
      border: 1px solid #d1d5db;
    }

    .btn-confirm-dialog {
      background: #b91c1c;
      color: #fff;
    }
  `],
})
export class ConfirmAttachmentDeleteDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<boolean>);
  protected readonly data = inject<ConfirmAttachmentDeleteDialogData>(MAT_DIALOG_DATA);

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}

